import {Injectable} from '@angular/core';
import {NotificationColor, NotificheService} from '../_services/notifiche.service';
import {buildEntityNotification, NotificationEntityType, StructuredNotificationMessage} from './entity-notification';
import {Contenitore} from '../_models/Contenitore';
import {Evento} from '../_models/Evento';
import {Movimento} from '../_models/Movimento';
import {Ordine} from '../_models/Ordine';
import {Risorsa} from '../_models/Risorsa';
import {Anagrafica} from '../_models/Anagrafica';
import {ControlloQualita} from '../_models/ControlloQualita';

type NotificationEntity = Contenitore | Evento | Movimento | Ordine | Risorsa | Anagrafica | ControlloQualita;
type EntityLike = Record<string, unknown>;

@Injectable({providedIn: 'root'})
export class SaveMessage {
  private readonly suppressUpdateAfterCreateMs = 3000;
  private readonly recentCreatedEntities = new Map<string, number>();

  /**
   * Modalità di sola visualizzazione (FR-043): sopprime le notifiche di **cambiamento di entità**
   * mentre una pagina di sola lettura è attiva. Il Sinottico è la prima pagina progettata per
   * restare incustodita su uno schermo condiviso, e le notifiche esistenti segnalano **ogni azione
   * di chiunque non sia l'utente corrente** — su un reparto attivo, decine di avvisi l'ora sopra le
   * tessere che la pagina esiste per far leggere.
   *
   * ⚠️ Interruttore **distinto** da {@link shouldSuppressUpdateNotification}, e non un suo
   * ampliamento: quello deduplica la sequenza *creato → aggiornato* sulle entità recenti, cioè
   * risponde a un'altra domanda. Piegarlo a questo uso farebbe dipendere una soppressione
   * **contestuale** da una logica di **deduplicazione temporale** — due responsabilità in un metodo
   * solo.
   *
   * Errori e avvisi (`messageError` / `messageWarning` / `messageInfo`) restano **sempre** attivi:
   * sono ciò che FR-022 e FR-040 vogliono far vedere da lontano.
   */
  private modalitaSolaVisualizzazione = false;

  constructor(private notificheService: NotificheService) {}

  /**
   * Attiva o disattiva la modalità di sola visualizzazione. Va disattivata **sia** in
   * `ionViewWillLeave` **sia** in `ngOnDestroy`: restare accesa dopo l'uscita renderebbe mute
   * **tutte** le altre pagine fino al ricaricamento dell'applicazione.
   */
  setModalitaSolaVisualizzazione(attiva: boolean) {
    this.modalitaSolaVisualizzazione = attiva;
  }

  get isModalitaSolaVisualizzazione(): boolean {
    return this.modalitaSolaVisualizzazione;
  }

  private static extractServerMessage(error: unknown): string | undefined {
    const message = (error as { error?: { message?: unknown } })?.error?.message;
    if (typeof message !== 'string') {
      return undefined;
    }

    const normalized = message.trim();
    return normalized.length > 0 ? normalized : undefined;
  }

  clearNotifications() {
    this.notificheService.clearAll();
  }

  messageInfo(label: string | StructuredNotificationMessage, showPopup = false) {
    this.notificheService.messageInfo(label, showPopup);
  }

  messageError(label: string | StructuredNotificationMessage) {
    this.notificheService.messageError(label);
  }

  /**
   * Mostra il messaggio che il backend ha inviato con l'errore, ripiegando su `fallback` quando non
   * ce n'è uno. Serve per gli errori che l'utente può correggere da solo: l'AuthApi risponde 400 con
   * `{ message }` — per esempio uno username già utilizzato, o coincidente con l'email di un altro
   * operatore — e senza propagarlo resta a video un generico "ci sono problemi", che non dice cosa
   * cambiare.
   */
  messageErrorFrom(error: unknown, fallback: string) {
    this.notificheService.messageError(SaveMessage.extractServerMessage(error) ?? fallback);
  }

  messageWarning(label: string | StructuredNotificationMessage) {
    this.notificheService.messageWarning(label);
  }

  entityCreated(entityType: NotificationEntityType, entity: NotificationEntity) {
    // La creazione resta tracciata anche in sola visualizzazione: la deduplicazione
    // creato → aggiornato deve continuare a funzionare all'uscita dalla pagina.
    this.trackRecentCreation(entityType, entity);
    if (this.modalitaSolaVisualizzazione) {
      return;
    }
    this.sendEntityNotification('created', entityType, entity, 'success');
  }

  entityUpdated(entityType: NotificationEntityType, entity: NotificationEntity) {
    if (this.modalitaSolaVisualizzazione || this.shouldSuppressUpdateNotification(entityType, entity)) {
      return;
    }
    this.sendEntityNotification('updated', entityType, entity, 'success');
  }

  entityDeleted(entityType: NotificationEntityType, entity: NotificationEntity) {
    if (this.modalitaSolaVisualizzazione) {
      return;
    }
    this.sendEntityNotification('deleted', entityType, entity, 'success');
  }

  entityEmptied(entityType: NotificationEntityType, entity: NotificationEntity) {
    this.sendEntityNotification('emptied', entityType, entity, 'warning');
  }

  private sendEntityNotification(
    action: 'created' | 'updated' | 'deleted' | 'emptied',
    entityType: NotificationEntityType,
    entity: NotificationEntity,
    color: NotificationColor
  ) {
    this.notificheService.message(buildEntityNotification(action, entityType, entity), color);
  }

  private trackRecentCreation(entityType: NotificationEntityType, entity: NotificationEntity): void {
    this.cleanupRecentCreations();
    const key = this.getEntityNotificationKey(entityType, entity);
    if (!key) {
      return;
    }

    this.recentCreatedEntities.set(key, Date.now());
  }

  private shouldSuppressUpdateNotification(entityType: NotificationEntityType, entity: NotificationEntity): boolean {
    this.cleanupRecentCreations();
    const key = this.getEntityNotificationKey(entityType, entity);
    if (!key) {
      return false;
    }

    const createdAt = this.recentCreatedEntities.get(key);
    if (!createdAt) {
      return false;
    }

    return Date.now() - createdAt <= this.suppressUpdateAfterCreateMs;
  }

  private cleanupRecentCreations(): void {
    const now = Date.now();
    this.recentCreatedEntities.forEach((createdAt, key) => {
      if (now - createdAt > this.suppressUpdateAfterCreateMs) {
        this.recentCreatedEntities.delete(key);
      }
    });
  }

  private getEntityNotificationKey(entityType: NotificationEntityType, entity: NotificationEntity): string | undefined {
    const source = entity as unknown as EntityLike;

    const primaryId = this.getNumber(source, 'id');
    if (primaryId !== undefined) {
      return `${entityType}:id:${primaryId}`;
    }

    switch (entityType) {
      case 'movimento': {
        const codBox = this.getString(source, 'codBox');
        if (codBox) {
          return `${entityType}:codBox:${codBox}`;
        }
        const rifOdP = this.getString(source, 'rifOdP');
        if (rifOdP) {
          return `${entityType}:rifOdP:${rifOdP}`;
        }
        return undefined;
      }
      case 'contenitore': {
        const code = this.getString(source, 'codice') ?? this.getString(source, 'codBox');
        return code ? `${entityType}:code:${code}` : undefined;
      }
      case 'evento': {
        const eventoKey = this.getString(source, 'rifOdP');
        if (eventoKey) {
          return `${entityType}:rifOdP:${eventoKey}`;
        }
        const idDocumento = this.getNumber(source, 'idDocumento');
        const idRiga = this.getNumber(source, 'idRiga');
        const idRigaLavorazione = this.getNumber(source, 'idRigaLavorazione');
        if (idDocumento !== undefined && idRiga !== undefined && idRigaLavorazione !== undefined) {
          return `${entityType}:ordine:${idDocumento}.${idRiga}.${idRigaLavorazione}`;
        }
        return undefined;
      }
      case 'ordine': {
        const rifOdP = this.getString(source, 'rifOdP');
        if (rifOdP) {
          return `${entityType}:rifOdP:${rifOdP}`;
        }
        const idDocumento = this.getNumber(source, 'idDocumento');
        const idRiga = this.getNumber(source, 'idRiga');
        const idRigaLavorazione = this.getNumber(source, 'idRigaLavorazione');
        if (idDocumento !== undefined && idRiga !== undefined && idRigaLavorazione !== undefined) {
          return `${entityType}:ordine:${idDocumento}.${idRiga}.${idRigaLavorazione}`;
        }
        return undefined;
      }
      case 'risorsa': {
        const codice = this.getString(source, 'codice');
        return codice ? `${entityType}:codice:${codice}` : undefined;
      }
      case 'controllo': {
        const controlloId = this.getNumber(source, 'controlloId');
        if (controlloId !== undefined) {
          return `${entityType}:controlloId:${controlloId}`;
        }
        const idEvento = this.getNumber(source, 'idEvento');
        if (idEvento !== undefined) {
          return `${entityType}:idEvento:${idEvento}`;
        }
        return undefined;
      }
    }
  }

  private getString(source: EntityLike, key: string): string | undefined {
    const value = source[key];
    if (typeof value !== 'string') {
      return undefined;
    }

    const normalized = value.trim();
    return normalized.length > 0 ? normalized : undefined;
  }

  private getNumber(source: EntityLike, key: string): number | undefined {
    const value = source[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
  }
}
